import React, { useState, useEffect } from 'react'
import Spinner from '../components/Spinner';
import {toast} from "react-toastify"
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import {getAuth} from "firebase/auth"
import {v4 as uuidv4} from "uuid";
import {addDoc, collection, serverTimestamp} from "firebase/firestore";
import {db} from "../firebase"
import { useNavigate } from 'react-router-dom';
import { currencies } from '../utils/currencies';

export default function CreateListing() {
  const navigate = useNavigate();
  const auth = getAuth()
  const [geolocationEnabled, setGeolocationEnabled] = useState(true);
  const[loading, setLoading] = useState(false);
  const [propertyType, setPropertyType] = useState("Houses and Apartments");
  const [imagePreviews, setImagePreviews] = useState([]);
  
  const [formData, setFormData] = useState({
    type: "rent",
    name: "",
    bedrooms: 1,
    bathrooms: 1,
    parking: false,
    furnished: false,
    address: "",
    description: "",
    offer: false,
    regularPrice: 0,
    discountedPrice: 0,
    latitude: 0,
    longitude: 0,
    images: {},
    currency: "USD",
    landSize: ""
  });

  const { type, name, bedrooms, bathrooms, parking, furnished, address, description, 
          offer, regularPrice, discountedPrice, latitude, longitude, images, currency, 
          landSize } = formData;

  function onChange(e){
    let boolean = null;
    if(e.target.value === "true"){
        boolean = true;
    }
    if(e.target.value === "false"){
        boolean = false;
    }
    
    // For files
    if(e.target.files){
      setFormData((prevState)=>({
        ...prevState,
        images: e.target.files
      }));
      
      // Create image previews
      const files = Array.from(e.target.files);
      const previews = files.map(file => URL.createObjectURL(file));
      setImagePreviews(previews);
    }
    
    // For text/Boolean/Number
    if(!e.target.files){
      setFormData((prevState)=>({
        ...prevState,
        [e.target.id]: boolean ?? e.target.value,
      }));
    }
  }

  function handlePropertyTypeChange(e) {
    setPropertyType(e.target.value);
  }

  async function onSubmit(e){
    e.preventDefault();
    setLoading(true);
    
    if(+discountedPrice >= +regularPrice){
      setLoading(false)
      toast.error("Discounted price needs to be less than regular price");
      return;
    }
    
    if(images.length > 6){
      setLoading(false)
      toast.error("Maximum 6 images are allowed");
      return;
    }
    
    let geolocation = {}
    let location
    if(geolocationEnabled){
      const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${address}&key=${process.env.REACT_APP_GEOCODE_API_KEY}`);
      const data = await response.json();
      geolocation.lat = data.results[0]?.geometry.location.lat ?? 0;
      geolocation.lng = data.results[0]?.geometry.location.lng ?? 0;

      location = data.status === "ZERO_RESULTS" && undefined;

      if(location === undefined){
        setLoading(false);
        toast.error("Please enter a correct address");
        return;
      }
    } else {
      geolocation.lat = latitude;
      geolocation.lng = longitude;
    }

    async function storeImage(image){
      return new Promise((resolve, reject)=>{
        const storage = getStorage();
        const filename = `${auth.currentUser.uid}-${image.name}-${uuidv4()}`;
        const storageRef = ref(storage, filename);
        const uploadTask = uploadBytesResumable(storageRef, image);
        uploadTask.on('state_changed', 
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log('Upload is ' + progress + '% done');
          }, 
          (error) => {
            reject(error)
          }, 
          () => {
            getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
              resolve(downloadURL);
            });
          }
        );
      })
    }

    const imgUrls = await Promise.all(
      [...images].map((image)=>storeImage(image))).catch((error)=>{
        setLoading(false)
        toast.error("Images not uploaded");
        return;
      }
    );

    const formDataCopy = {
      ...formData,
      imgUrls,
      geolocation,
      timestamp: serverTimestamp(),
      userRef: auth.currentUser.uid,
      propertyType,
    };
    
    delete formDataCopy.images;
    !formDataCopy.offer && delete formDataCopy.discountedPrice;
    delete formDataCopy.latitude;
    delete formDataCopy.longitude;
    
    // Remove landSize if not applicable
    if (propertyType === "Houses and Apartments" || propertyType === "Commercial Property") {
      delete formDataCopy.landSize;
    }
    
    const docRef = await addDoc(collection(db, "listings"), formDataCopy);
    setLoading(false)
    toast.success("Listing created");
    navigate(`/category/${formDataCopy.type}/${docRef.id}`);
  }

  if(loading){
    return <Spinner />;
  }

  return (
    <main className="max-w-md px-2 mx-auto">
      <h1 className="text-3xl text-center mt-6 font-bold">Create a Listing</h1>
      <form onSubmit={onSubmit}>
        <p className="text-lg mt-6 font-semibold">Property Type</p>
        <select
          value={propertyType}
          onChange={handlePropertyTypeChange}
          required
          className="w-full px-4 py-2 text-xl text-gray-700 bg-white border 
          border-gray-300 rounded transition duration-150 ease-in-out mb-6"
        >
          <option value="Houses and Apartments">Houses and Apartments</option>
          <option value="Commercial Property">Commercial Property</option>
          <option value="Residential land">Residential land</option>
          <option value="Agricultural land">Agricultural land</option>
          <option value="Commercial land">Commercial land</option>
        </select>

        <p className="text-lg mt-6 font-semibold">Sell / Rent</p>
        <div className="flex">
          <button type='button' id='type' value="sale" onClick={onChange} className={`mr-3 px-7 py-3 
            font-medium text-sm uppercase shadow-md rounded hover:shadow-lg 
            focus:shadow-lg active:shadow-lg transition duration-150 ease-in-out w-full ${
                type === "rent" ? "bg-white text-black" : "bg-[#09848B] text-white"
            }`}>
            sell
          </button>

          <button type='button' id='type' value="rent" onClick={onChange} className={`ml-3 px-7 py-3 
            font-medium text-sm uppercase shadow-md rounded hover:shadow-lg 
            focus:shadow-lg active:shadow-lg transition duration-150 ease-in-out w-full ${
                type === "sale" ? "bg-white text-black" : "bg-[#09848B] text-white"
            }`}>
            rent
          </button>
        </div>

        <p className="text-lg mt-6 font-semibold">Name</p>
        <input type="text" id="name" value={name} onChange={onChange} 
        placeholder="Property title / Max 50 characters" maxLength={32} 
        minLength={10} required 
        className="w-full px-4 py-2 text-xl text-gray-700 bg-white border 
        border-gray-300 rounded transition duration-150 ease-in-out focus:text-gray-700 
        focus:bg-white focus:border-[#09848B] mb-6"/>

        {(propertyType === "Houses and Apartments" || propertyType === "Commercial Property") && (
          <div className="flex space-x-6 mb-6">
            {propertyType === "Houses and Apartments" && (
              <>
                <div>
                  <p className="text-lg font-semibold">Beds</p>
                  <input type="number" id='bedrooms' value={bedrooms} onChange={onChange} 
                  min={1} max={50} className="w-full px-4 py-2 text-xl text-gray-700 bg-white 
                  border border-gray-300 rounded transition duration-150 ease-in-out 
                  focus:text-gray-700 focus:bg-white focus:border-[#09848B] text-center"/>
                </div>
                <div>
                  <p className="text-lg font-semibold">Baths</p>
                  <input type="number" id='bathrooms' value={bathrooms} onChange={onChange} 
                  min={1} max={50} className="w-full px-4 py-2 text-xl text-gray-700 bg-white 
                  border border-gray-300 rounded transition duration-150 ease-in-out 
                  focus:text-gray-700 focus:bg-white focus:border-[#09848B] text-center"/>
                </div>
              </>
            )}
          </div>
        )}

        {(propertyType === "Houses and Apartments" || propertyType === "Commercial Property") && (
          <>
            <p className="text-lg mt-6 font-semibold">Parking spot</p>
            <div className="flex">
              <button type='button' id='parking' value={true} onClick={onChange} className={`mr-3 px-7 py-3 
                font-medium text-sm uppercase shadow-md rounded hover:shadow-lg 
                focus:shadow-lg active:shadow-lg transition duration-150 ease-in-out w-full ${
                    !parking ? "bg-white text-black" : "bg-[#09848B] text-white"
                }`}>
                Yes
              </button>

              <button type='button' id='parking' value={false} onClick={onChange} className={`ml-3 px-7 py-3 
                font-medium text-sm uppercase shadow-md rounded hover:shadow-lg 
                focus:shadow-lg active:shadow-lg transition duration-150 ease-in-out w-full ${
                    parking ? "bg-white text-black" : "bg-[#09848B] text-white"
                }`}>
                No
              </button>
            </div>
          </>
        )}

        {propertyType === "Houses and Apartments" && (
          <>
            <p className="text-lg mt-6 font-semibold">Furnished</p>
            <div className="flex">
              <button type='button' id='furnished' value={true} onClick={onChange} className={`mr-3 px-7 py-3 
                font-medium text-sm uppercase shadow-md rounded hover:shadow-lg 
                focus:shadow-lg active:shadow-lg transition duration-150 ease-in-out w-full ${
                    !furnished ? "bg-white text-black" : "bg-[#09848B] text-white"
                }`}>
                Yes
              </button>

              <button type='button' id='furnished' value={false} onClick={onChange} className={`ml-3 px-7 py-3 
                font-medium text-sm uppercase shadow-md rounded hover:shadow-lg 
                focus:shadow-lg active:shadow-lg transition duration-150 ease-in-out w-full ${
                    furnished ? "bg-white text-black" : "bg-[#09848B] text-white"
                }`}>
                No
              </button>
            </div>
          </>
        )}

        {(propertyType === "Residential land" || propertyType === "Agricultural land" || propertyType === "Commercial land") && (
          <div className="mb-6">
            <p className="text-lg font-semibold">Size of land</p>
            <input type="text" id="landSize" value={landSize} onChange={onChange} 
            placeholder="e.g. 1000 sq ft" required 
            className="w-full px-4 py-2 text-xl text-gray-700 bg-white border 
            border-gray-300 rounded transition duration-150 ease-in-out focus:text-gray-700 
            focus:bg-white focus:border-[#09848B]"/>
          </div>
        )}

        <p className="text-lg mt-6 font-semibold">Address</p>
        <textarea type="text" id="address" value={address} onChange={onChange} 
        placeholder="Address"
        required 
        className="w-full px-4 py-2 text-xl text-gray-700 bg-white border 
        border-gray-300 rounded transition duration-150 ease-in-out focus:text-gray-700 
        focus:bg-white focus:border-[#09848B] mb-6"
        />

        {!geolocationEnabled && (
          <div className='flex space-x-6 justify-start mb-6'>
            <div className=''>
              <p className='text-lg font-semibold'>Latitude</p>
              <input type="number" id="latitude" value={latitude} onChange={onChange} required min={-90} max={90}
              className='w-full px-4 py-2 text-xl text-gray-700 bg-white border border-gray-300 
              rounded transition duration-150 ease-in-out focus:bg-white 
              focus:text-gray-700 focus:border-[#09848B] text-center'/>
            </div>
            <div className=''>
              <p className='text-lg font-semibold'>Longitude</p>
              <input type="number" id="longitude" value={longitude} onChange={onChange} required min={-180} max={180} 
              className='w-full px-4 py-2 text-xl text-gray-700 bg-white border border-gray-300 
              rounded transition duration-150 ease-in-out focus:bg-white 
              focus:text-gray-700 focus:border-[#09848B] text-center'/>
            </div>
          </div>
        )}

        <p className="text-lg font-semibold">Description</p>
        <textarea type="text" id="description" value={description} onChange={onChange} 
        placeholder="Description"
        required 
        className="w-full px-4 py-2 text-xl text-gray-700 bg-white border 
        border-gray-300 rounded transition duration-150 ease-in-out focus:text-gray-700 
        focus:bg-white focus:border-[#09848B] mb-6"/>

        <p className="text-lg font-semibold">Offer</p>
        <div className="flex mb-6">
          <button type='button' id='offer' value={true} onClick={onChange} className={`mr-3 px-7 py-3 
            font-medium text-sm uppercase shadow-md rounded hover:shadow-lg 
            focus:shadow-lg active:shadow-lg transition duration-150 ease-in-out w-full ${
                !offer ? "bg-white text-black" : "bg-[#09848B] text-white"
            }`}>
            Yes
          </button>

          <button type='button' id='offer' value={false} onClick={onChange} className={`ml-3 px-7 py-3 
            font-medium text-sm uppercase shadow-md rounded hover:shadow-lg 
            focus:shadow-lg active:shadow-lg transition duration-150 ease-in-out w-full ${
                offer ? "bg-white text-black" : "bg-[#09848B] text-white"
            }`}>
            No
          </button>
        </div>

        <div className="mb-6">
  <p className="text-lg font-semibold">Currency</p>
  <select
    id="currency"
    value={currency}
    onChange={onChange}
    required
    className="w-full px-4 py-2 text-xl text-gray-700 bg-white border 
    border-gray-300 rounded transition duration-150 ease-in-out mb-4"
  >
    {currencies.map((curr) => (
      <option key={curr.code} value={curr.code}>
        {curr.code} - {curr.name}
      </option>
    ))}
  </select>

  <p className="text-lg font-semibold">Regular price</p>
  <div className="flex w-full justify-center items-center">
    <input 
      type="number" 
      id="regularPrice" 
      value={regularPrice} 
      onChange={onChange} 
      min={50} 
      required 
      className="w-full px-4 py-2 text-xl text-gray-700 bg-white border border-gray-300 rounded 
      transition duration-150 ease-in-out focus:text-gray-700 focus:bg-white 
      focus:border-[#09848B] text-center"
    />
    {type === "rent" && (
      <div className='ml-4'>
        <p className="text-md w-full whitespace-nowrap">/ month</p>
      </div>
    )}
  </div>
</div>

{offer && (
  <div className="mb-6">
    <p className="text-lg font-semibold">Discounted price</p>
    <div className="flex w-full justify-center items-center">
      <input 
        type="number" 
        id="discountedPrice" 
        value={discountedPrice} 
        onChange={onChange} 
        min={50} 
        required={offer} 
        className="w-full px-4 py-2 text-xl text-gray-700 bg-white border border-gray-300 rounded 
        transition duration-150 ease-in-out focus:text-gray-700 focus:bg-white 
        focus:border-[#09848B] text-center"
      />
      {type === "rent" && (
        <div className='ml-4'>
          <p className="text-md w-full whitespace-nowrap">/ month</p>
        </div>
      )}
    </div>
  </div>
)}

        <div className="mb-6">
          <p className="text-lg font-semibold">Images</p>
          <p className="text-gray-600">The first image will be the cover (Max 6)</p>
          <input type="file" id="images" onChange={onChange} accept=".jpg, .png, .jpeg" 
          multiple required className="w-full px-3 py-1.5 text-gray-700 bg-white border 
          border-gray-300 rounded transition duration-150 ease-in-out 
          focus:bg-white focus:border-[#09848B]" />

          {imagePreviews.length > 0 && (
            <div className="flex flex-wrap mt-4 gap-2">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="w-24 h-24 relative">
                  <img 
                    src={preview} 
                    alt={`Preview ${index}`} 
                    className="w-full h-full object-cover rounded border border-gray-300"
                  />
                  <span className="absolute top-0 right-0 bg-black text-white text-xs px-1 rounded-full">
                    {index + 1}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <button type="submit" className="mb-6 w-full px-7 py-3 bg-blue-600 
        text-white font-medium text-sm uppercase rounded shadow-md 
        hover:bg-[#09848B] hover:shadow-lg focus:bg-[#09848B] focus:shadow-lg 
        active:bg-blue-800 active:shadow-lg transition duration-150 ease-in-out">
          Create Listing
        </button>
      </form>
    </main>
  )
}